import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { HomeIcon, MapIcon, UserIcon } from '@heroicons/react/24/outline';
import { HomeIcon as HomeIconSolid, MapIcon as MapIconSolid, UserIcon as UserIconSolid } from '@heroicons/react/24/solid';

import { selectAuthState } from '../../redux/selectors/authSelectors';
import { selectAllCartItems } from '../../redux/slices/cartSlice';
import { useGetUnreadCountQuery } from '../../redux/apis/endpoints/messageApi';
import { CartIcon, MessageIcon } from './ActionItems';

const BottomNav = () => {
  const location = useLocation();
  const [activeItem, setActiveItem] = useState('');
  
  const { isAuthenticated: isLoggedIn } = useSelector(selectAuthState);
  
  const allCartItems = useSelector(selectAllCartItems);

  const { data: unreadCount } = useGetUnreadCountQuery(undefined, {
    skip: !isLoggedIn,
  });

  const cartNum = React.useMemo(() => {
    if (!isLoggedIn) return 0;
    return allCartItems?.length || 0;
  }, [isLoggedIn, allCartItems]);

  useEffect(() => {
    const menus = [
      { path: '/browse', name: 'Home' },
      { path: '/map', name: 'Map' },
      { path: '/cart', name: 'Cart' },
      { path: '/message', name: 'Messages' },
      { path: '/dashboard', name: 'Profile' },
    ];

    const pathname = location.pathname.toLowerCase();
    
    // Default to Home if root path
    if (pathname === '/' || pathname === '/browse') {
      setActiveItem('Home');
      return;
    }

    const match = menus.find((each) => pathname.startsWith(each.path));
    setActiveItem(match ? match.name : '');
  }, [location.pathname]);

  // If not logged in, we might not show the full bottom nav, or we might redirect them to login.
  // Assuming we show at least Home and Map, and maybe 'Sign In' for Profile.
  
  const navItems = [
    {
      name: 'Home',
      link: '/browse',
      iconOutline: <HomeIcon className="h-6 w-6" />,
      iconSolid: <HomeIconSolid className="h-6 w-6" />,
      show: true,
    },
    // {
    //   name: 'Map',
    //   link: '/map',
    //   iconOutline: <MapIcon className="h-6 w-6" />,
    //   iconSolid: <MapIconSolid className="h-6 w-6" />,
    //   show: true,
    // },
    {
      name: 'Messages',
      link: '/message',
      // We wrap the ActionItems icons because they handle badges
      iconOutline: <MessageIcon unreadCount={unreadCount || 0} className="h-6 w-6" />,
      iconSolid: <MessageIcon unreadCount={unreadCount || 0} className="h-6 w-6" />,
      show: isLoggedIn,
    },
    {
      name: 'Cart',
      link: '/cart',
      iconOutline: <CartIcon cartCount={cartNum} className="h-6 w-6" />,
      iconSolid: <CartIcon cartCount={cartNum} className="h-6 w-6" />,
      show: true,
    },
    {
      name: 'Profile',
      link: isLoggedIn ? '/dashboard' : '/login',
      iconOutline: <UserIcon className="h-6 w-6" />,
      iconSolid: <UserIconSolid className="h-6 w-6" />,
      show: true,
    }
  ].filter(item => item.show);

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 flex justify-around items-center pt-2"
      style={{
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)'
      }}
    >
      {navItems.map((item) => {
        const isActive = activeItem === item.name;
        // Use explicit teal hex code to prevent any blue fallback
        const activeColor = '#00b5ad'; 
        
        return (
          <Link
            key={item.name}
            to={item.link}
            className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${
              isActive ? '' : 'text-gray-500 hover:text-gray-900'
            }`}
            style={isActive ? { color: activeColor } : {}}
          >
            <div className={isActive ? '' : 'text-gray-500'} style={isActive ? { color: activeColor } : {}}>
              {isActive ? item.iconSolid : item.iconOutline}
            </div>
            <span className="text-[10px] font-medium tracking-wide">
              {item.name}
            </span>
          </Link>
        );
      })}
    </div>
  );
};

export default BottomNav;
